import React, { useState } from "react";
import EventItem from "../../../items/EventItem";
import EventImage01 from "../../../../assets/images/event_01.webp";
import EventImage02 from "../../../../assets/images/event-02.webp";
import EventImage03 from "../../../../assets/images/event-03.webp";
import EventImage04 from "../../../../assets/images/event-04.webp";
import EventImage05 from "../../../../assets/images/event-05.webp";
import EventImage06 from "../../../../assets/images/event-06.webp";
import {
  keepPreviousData,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { eventList } from "../../../../services/eventService";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import { lngDetect } from "../../../../helpers/lngDetect";
import { useDebounce } from "../../../../hooks/useDebounce";
import { useSelector } from "react-redux";
import Button from "../../../form/Button";
import EventItemLoader from "../../../loader/EventItemLoader";
import noDataImage from "../../../../assets/images/tobaco-no.svg";
import NoData from "../../../elements/Nodata";
import PopoverDropdown from "../../../elements/PopoverDropdown";
import Search from "../../../form/Search";
import Select from "../../../form/select/Select";
import { t } from "i18next";

const HubEvents = ({ selectedProfile = {}, setTotalEvents = () => {} }) => {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState({
    limit: 8,
    page: 1,
    sortQuery: "startDate",
  });
  const { lang } = useSelector((state) => state?.langProvider);
  const [locationkeyword, setlocationKeyword] = useState("");
  const locationsearch = useDebounce(locationkeyword, 300);
  const [keyword, setKeyword] = useState("");
  const search = useDebounce(keyword, 300);

  const {
    data: events,
    isLoading: eventLoading,
    isFetching: eventFetching,
  } = useQuery({
    queryKey: [
      "event-hub",
      selectedProfile?.value,
      filters,
      search,
      locationsearch,
    ],
    enabled: !!selectedProfile?.value,
    queryFn: async () => {
      const filter_data = {
        ...filters,
        ...filters?.filterBy,
      };
      delete filter_data?.filterBy;
      const res = await eventList({
        published: true,
        organization: selectedProfile?.value,
        ...filter_data,
        ...(search && { keyword: search }),
        ...(locationsearch && { address: locationsearch }),
      });
      if (res?.status !== 200) {
        toast.warning(res?.message);
      }

      if (res?.docs?.status === 200) {
        setTotalEvents(res?.docs?.metadata?.totalDocs);
        const new_data_arr = res?.docs?.data?.map((item) => {
          const date = `${
            item?.startDate
              ? dayjs(item?.startDate).format(
                  item?.endDate ? "DD" : "DD MMM YYYY"
                )
              : ""
          }${
            item?.endDate
              ? " - " + dayjs(item?.endDate).format("DD MMM YYYY")
              : ""
          }`;
          return {
            ...item,
            id: item?._id,
            eventTitle: lngDetect(item?.title),
            eventDate: date || "",
            eventLocation: item?.address?.name,
            eventMinRate: item?.minTicketPrice || false,
            images: item?.gallery?.map((img) => img?.url) || [],
            tags: item?.tagData?.map((item) => lngDetect(item?.name)) || [],
            link: `/event-details/${item?.uid}`,
          };
        });

        if (search || filters.page === 1) {
          return {
            ...res?.docs,
            data: [...new_data_arr],
            profile: selectedProfile?.value,
          };
        } else {
          if (events?.profile !== selectedProfile?.value) {
            queryClient.setQueriesData(
              { queryKey: ["event-hub"], exact: false },
              (state) => {
                return {};
              }
            );
            return {
              ...res?.docs,
              data: [...new_data_arr],
              profile: selectedProfile?.value,
            };
          } else {
            return {
              ...res?.docs,
              data: [...(events?.data || []), ...new_data_arr]?.filter(
                (item, index, self) =>
                  index === self.findIndex((t) => t?._id === item?._id)
              ),
              profile: selectedProfile?.value,
            };
          }
        }
      } else {
        return {
          ...res,
          data: [],
          profile: selectedProfile?.value,
        };
      }
    },
    ...(!filters?.filterBy && {
      placeholderData: keepPreviousData,
    }),
  });

  const loadMore = () => {
    if (events?.metadata?.hasNextPage) {
      setFilters((pre) => ({
        ...pre,
        page: events?.metadata?.nextPage,
      }));
    }
  };

  function addToFavoriteResponse(res) {
    if (res?.status === 200) {
      queryClient.setQueryData(
        ["event-hub", selectedProfile?.value, filters, search, locationsearch],
        (state) => {
          const state_data = JSON.parse(JSON.stringify(state || {}));
          const new_arr = state_data?.data?.map((item) =>
            item?._id === res?.data?._id ? { ...item, ...res?.data } : item
          );
          return { ...state_data, data: new_arr };
        }
      );
    }
  }

  return (
    <>
      <div className="flex justify-end mb-6">
        <PopoverDropdown
          buttonLabel={
            <div className="flex items-center gap-3">
              <i className="fa-regular fa-sliders-up"></i>
              {t("Filter & Sort")}
            </div>
          }
          contentBodyClass={"z-[1] mt-1 w-72"}
          contentClass={"space-y-5"}
        >
          <div className="font-medium text-base">
            {t("Filter & Sort Options")}
          </div>
          <div className="space-y-3">
            <Search
              divClasses={"h-10 rounded-lg"}
              placeholder={t("Search by name")}
              search={(e) => {
                setFilters((pre) => ({ ...pre, page: 1 }));
                setKeyword(e);
              }}
              searchValue={keyword}
            />
            <Search
              divClasses={"h-10 rounded-lg"}
              placeholder={t("search by location")}
              search={(e) => {
                setFilters((pre) => ({ ...pre, page: 1 }));
                setlocationKeyword(e);
              }}
              searchValue={locationkeyword}
            />
            <Select
              dropdownButtonClass={"rounded-lg h-10"}
              selectedValue={filters?.sortQuery || "startDate"}
              dropdownData={[
                { name: "sort by name", value: "title" },
                { name: "sort by date", value: "startDate" },
              ]}
              onChange={(val) => {
                setFilters((pre) => ({ ...pre, page: 1, sortQuery: val }));
              }}
            />
            <Button
              buttonLabel={t("Clear all filters")}
              buttonClasses={"w-full"}
              buttonFunction={() => {
                setFilters((pre) => ({ ...pre, sortQuery: "startDate" }));
                setKeyword("");
                setlocationKeyword("");
              }}
            />
          </div>
        </PopoverDropdown>
      </div>
      {eventLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {Array.from({ length: 8 }, (_, i) => (
            <EventItemLoader key={i} />
          ))}
        </div>
      ) : events?.data?.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {events?.data?.map((item, index) => (
            <EventItem
              key={item?.id || item?._id}
              data={item}
              addToFavoriteResponse={addToFavoriteResponse}
            />
          ))}
        </div>
      ) : (
        events?.data?.length === 0 &&
        !eventLoading && (
          <NoData image={noDataImage} message="No events found" />
        )
      )}
      {events?.data?.length > 0 &&
        events?.data?.length < events?.metadata?.totalDocs && (
          <div className="flex space-y-4 items-center justify-between">
            <div className="text-deep-purple text-xl 2xl:text-2xl font-semibold text-center">
              {t("Showing")} {events?.data?.length} {t("of")}{" "}
              {events?.metadata?.totalDocs}
            </div>
            {events?.metadata?.hasNextPage && (
              <div className="flex justify-center">
                <Button
                  buttonLabel={t("Load More")}
                  buttonLabelClasses="text-lg uppercase text-white"
                  buttonClasses="h-14 bg-secondary-gradient"
                  buttonFunction={loadMore}
                  {...(eventFetching && {
                    loading: true,
                    buttonIconPosition: "left",
                    isDisable: true,
                  })}
                />
              </div>
            )}
          </div>
        )}
    </>
  );
};

export default HubEvents;
